import { useMemo, useCallback, useState } from 'react';
import { clearCache } from 'ahooks';
import { useRequestController } from '../../Home/hooks/useRequestController.ts';
import { useDownloadNotification } from '../../ChatPage/components/DownloadLink/hooks';
import { downloadFile } from '@home/utils/file';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import {
  ISkillData,
  ICategoryTab,
  IGetSkillsListResponse,
  IGetSkillsListParams,
  IGetSkillCategoriesListResponse,
  ISkillCategoryData,
  ALL_CATEGORY,
  ESkillActionType,
} from '../types/index';
import { getSkillPackageVersion } from '../utils/display';


/** 从分类接口数据构建分类 TAB（接口为空时仅保留“全部”） */
const buildCategoryTabs = (categories: ISkillCategoryData[], isEN: boolean): ICategoryTab[] => {
  const tabs: ICategoryTab[] = [{ key: ALL_CATEGORY, label: isEN ? 'All' : '全部' }];

  if (!categories || categories.length === 0) {
    return tabs;
  }

  categories.forEach(({ category_id, category, category_en, category_icon }) => {
    if (!category_id) {
      return;
    }
    tabs.push({
      key: category_id,
      label: isEN ? (category_en || category) : category,
      icon: category_icon,
    });
  });

  return tabs;
};


const SKILLS_LIST_CACHE_KEY = 'SkillsList';
const SKILLS_CATEGORIES_LIST_CACHE_KEY = 'SkillsCategoriesList';


/** 获取 Skills 数据的 Hook */
export const useSkillsData = (isEN = false) => {
  const categoriesQuery = useMemo(() => ({ page: 1, page_size: 100, all: 1 }), []);
  /** list 带 version，后端按环境映射后统一以 file_url 返回 */
  const listQuery = useMemo<IGetSkillsListParams>(
    () => ({ version: getSkillPackageVersion() }),
    [],
  );

  const { data: listData, loading, refresh } = useRequestController({
    method: 'GET', url: '/api/v1/util/skills/list', cacheKey: SKILLS_LIST_CACHE_KEY,
    refreshDeps: listQuery,
    staleTime: 1800000,
    // 组件已卸载的时候也不取消请求，请求完成后回填缓存，下次进入直接命中
    leaveRequest: true,
  });
  const { data: categoriesData } = useRequestController({
    method: 'GET',
    url: '/api/v1/util/skills/categories/list',
    cacheKey: SKILLS_CATEGORIES_LIST_CACHE_KEY,
    refreshDeps: categoriesQuery,
    staleTime: 1800000,
  });


  const { runAsync: recordDownload } = useRequestController({ method: 'POST', url: '/api/v1/util/skills/download', manual: true });
  const { setNoticeStatus } = useDownloadNotification();


  /** 正在下载中的 skill ID 集合，基于具体 ID 独立判定 */
  const [downloadingSet, setDownloadingSet] = useState<Set<string>>(new Set());

  /** 判断某个 skill 是否正在下载 */
  const isSkillDownloading = useCallback((skillId: string) => downloadingSet.has(skillId), [downloadingSet]);

  /** 清除缓存后强制刷新列表 */
  const forceRefresh = useCallback(() => {
    clearCache(SKILLS_LIST_CACHE_KEY);
    return refresh();
  }, [refresh]);

  const allSkills = useMemo(() => {
    return (listData as IGetSkillsListResponse)?.list ?? [];
  }, [listData]);

  const skillCategories = useMemo(() => {
    return (categoriesData as IGetSkillCategoriesListResponse)?.list ?? [];
  }, [categoriesData]);

  const installedSkills = useMemo(() => allSkills.filter((s) => s.is_installed), [allSkills]);
  const downloadedSkills = useMemo(() => allSkills.filter((s) => s.is_downloaded), [allSkills]);
  /** 已安装的 Skills（API 格式，用于 skill_config.allowed_skills） */
  const allowedSkillsForApi = useMemo(() => installedSkills.map((s) => ({ name: s.skill_name })), [installedSkills]);
  const categoryTabs = useMemo(() => buildCategoryTabs(skillCategories, isEN), [skillCategories, isEN]);

  const handleInstallSkill = useCallback(async (skillId: string) => {
    await recordDownload({
      skill_id: skillId,
      version: getSkillPackageVersion(),
      action_type: ESkillActionType.INSTALL,
      client_info: 'PC',
    });
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '700501058',
      extInfo: JSON.stringify({ action: 'install', skill_id: skillId }),
      typeId: 'aigc',
    });
    forceRefresh();
  }, [recordDownload, forceRefresh]);

  const handleDownloadSkill = useCallback(async (skill: ISkillData) => {
    const skillId = skill.skill_id;
    // 如果该条数据正在下载中，仅该条禁止再次点击
    if (downloadingSet.has(skillId)) return;

    // 将当前 skillId 加入下载中集合
    setDownloadingSet((prev) => new Set(prev).add(skillId));
    setNoticeStatus('loading');
    try {
      const res = await recordDownload({
        skill_id: skillId,
        version: getSkillPackageVersion(),
        action_type: ESkillActionType.DOWNLOAD,
        client_info: 'PC',
      });
      const downloadUrl = res?.download_url;
      if (downloadUrl) {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        downloadFile(blob, `${res?.skill_name || skill.skill_name}.zip`);
        setNoticeStatus('success');
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501059',
          extInfo: JSON.stringify({ action: 'download', skill_id: skillId, skill_name: skill.skill_name}),
          typeId: 'aigc',
        });
      } else {
        setNoticeStatus('error');
        buriedBtnPoint({
          pageId: '700501',
          buttonId: '700501059',
          extInfo: JSON.stringify({ action: 'download', skill_id: skillId, skill_name: skill.skill_name}),
          typeId: 'aigc',
        });
      }
    } catch {
      setNoticeStatus('error');
      buriedBtnPoint({
        pageId: '700501',
        buttonId: '700501018',
        extInfo: JSON.stringify({ action: 'download', skill_id: skillId, skill_name: skill.skill_name, status: 'error', reason: 'exception' }),
        typeId: 'aigc',
      });
    } finally {
      // 下载完成后从集合中移除
      setDownloadingSet((prev) => {
        const next = new Set(prev);
        next.delete(skillId);
        return next;
      });
    }
  }, [downloadingSet, recordDownload, setNoticeStatus]);

  const getFilteredSkills = useCallback((category: string, keyword: string, isENLang: boolean) => {
    return allSkills
      .filter((s) => category === ALL_CATEGORY || s.category_id === category)
      .filter((s) => {
        if (!keyword.trim()) return true;
        const kw = keyword.toLowerCase();
        const blob = (
          isENLang
            ? [s.display_name_en, s.description_en]
            : [s.display_name_cn, s.description]
        )
          .filter((x) => x && String(x).trim())
          .join(' ')
          .toLowerCase();
        return blob.includes(kw);
      });
  }, [allSkills]);

  return {
    loading,
    allSkills,
    installedSkills,
    downloadedSkills,
    /** 已安装的 Skills（API 格式，用于 skill_config.allowed_skills） */
    allowedSkillsForApi,
    categoryTabs,
    handleInstallSkill,
    handleDownloadSkill,
    getFilteredSkills,
    refreshSkills: forceRefresh,
    isSkillDownloading,
  };
};
